import { FATHOM_EVENTS } from '@/lib/fathom/fathom-events';
import {
  AI_NATIVE_SDLC_DATE_PUBLISHED,
  AI_NATIVE_SDLC_PUBLISHED_LABEL,
  AI_NATIVE_SDLC_STATUS,
  AI_NATIVE_SDLC_SUMMARY,
  AI_NATIVE_SDLC_TITLE,
} from './publish-status';
import { PAPER } from './paper';

const events = FATHOM_EVENTS.resources['ebooks-and-whitepapers'];

// The lede's first sentence: the paper in one line (the catalog and homepage cards).
const SUMMARY = AI_NATIVE_SDLC_SUMMARY;

const LEDE = `${SUMMARY} This paper models the AI-native SDLC in eight stages, from Intent to Maintain, and looks deeply at verification, the basis for trusting what agents build.`;

// The author, their job title (JSON-LD, with worksFor the Organization) and their byline (the
// PDF cover's): paper.ts, shared with the PDF.
const AUTHOR = PAPER.author;
const AUTHOR_JOB_TITLE = PAPER.authorJobTitle;
const AUTHOR_ROLE = PAPER.authorRole;

// The PDF, ungated and hosted with the site (public/). Built from the markdown: see print/README.md.
const PDF_URL = '/assets/pdfs/a-model-of-the-ai-native-sdlc.pdf';

// Page copy is Harriet's "Whitepaper reader" design (31 Oct), verbatim: the paper is read ungated
// on the page (its text in ./whitepaper/a-model-of-the-ai-native-sdlc.md) and the PDF downloads directly.
export const aiNativeSdlcContent = {
  status: AI_NATIVE_SDLC_STATUS,
  datePublished: AI_NATIVE_SDLC_DATE_PUBLISHED,
  title: AI_NATIVE_SDLC_TITLE,
  /** The byline: the meta table's Author row (with their role) and the JSON-LD author (with
   * their job title, working for the Organization). */
  author: AUTHOR,
  authorRole: AUTHOR_ROLE,
  authorJobTitle: AUTHOR_JOB_TITLE,
  eyebrow: 'Whitepaper',
  lede: LEDE,
  summary: SUMMARY,
  /** Labelled rows in the hero's meta table (the desktop reader design's). */
  meta: [
    { label: 'Author', value: AUTHOR, detail: AUTHOR_ROLE },
    { label: 'Chapters', value: '6, plus summary' },
    { label: 'Read time', value: '45 min' },
    { label: 'Published', value: AI_NATIVE_SDLC_PUBLISHED_LABEL },
  ],
  /** The CSS cover in the hero; it starts reading. */
  cover: {
    eyebrow: 'Whitepaper',
    site: 'develocity.ai',
  },
  /**
   * Link-share (OpenGraph, and Twitter/X via Next's inheritance) image, also the page's JSON-LD
   * image: the page's CSS cover (as at 1440px, zoomed 1.43×) centred over the hero mosaic,
   * screenshotted at 1200×630 (inside the square crop some apps use). Re-render it, the homepage
   * card thumb (880×550, zoom 1.17) and the catalog card (no cover text) if the cover changes.
   */
  shareImage: {
    url: '/assets/images/opengraph/ai-native-sdlc-whitepaper.jpg',
    width: 1200,
    height: 630,
    alt: 'Cover of the Develocity whitepaper "A Model of the AI-Native SDLC" on a teal mosaic background',
  },
  /** The reader: hero actions, contents, sharing, figures, reading analytics, closing band. */
  reader: {
    startReading: {
      label: 'Start reading',
      href: '#read',
      fathomEvent: events.ai_native_sdlc_reader.start_reading.click,
    },
    coverLabel: `Start reading: ${AI_NATIVE_SDLC_TITLE}`,
    progressLabel: 'Reading progress',
    downloadPdf: {
      label: 'Download PDF',
      href: PDF_URL,
      fathomEvent: events.ai_native_sdlc_reader.download_pdf.click,
    },
    coverEvent: events.ai_native_sdlc_reader.cover.click,
    share: {
      label: 'Share',
      copiedLabel: 'Link copied',
      ariaLabel: 'Share: copy a link to this page',
      copiedAriaLabel: 'Link copied to clipboard',
      heroEvent: events.ai_native_sdlc_reader.share.hero.click,
      contentsEvent: events.ai_native_sdlc_reader.share.contents.click,
      barEvent: events.ai_native_sdlc_reader.share.bar.click,
      sheetEvent: events.ai_native_sdlc_reader.share.sheet.click,
      copiedEvent: events.ai_native_sdlc_reader.share.copied,
      nativeEvent: events.ai_native_sdlc_reader.share.native,
    },
    contents: {
      label: 'Contents',
      readSuffix: '% read',
      fathomEvent: events.ai_native_sdlc_reader.contents.click,
      subsectionEvent: events.ai_native_sdlc_reader.contents.subsection.click,
      openEvent: events.ai_native_sdlc_reader.contents.open,
      sheetEvent: events.ai_native_sdlc_reader.contents.sheet.click,
    },
    /** Reading depth (share of the paper) and sections reached, each once per page view. */
    tracking: {
      depth: [
        { percent: 25, fathomEvent: events.ai_native_sdlc_reader.depth[25] },
        { percent: 50, fathomEvent: events.ai_native_sdlc_reader.depth[50] },
        { percent: 75, fathomEvent: events.ai_native_sdlc_reader.depth[75] },
        { percent: 100, fathomEvent: events.ai_native_sdlc_reader.depth[100] },
      ],
      sections: events.ai_native_sdlc_reader.section,
      noteEvent: events.ai_native_sdlc_reader.note.click,
      citationEvent: events.ai_native_sdlc_reader.citation.click,
      siteLinkEvent: events.ai_native_sdlc_reader.site_link.click,
    },
    figure: {
      expandLabel: 'Expand',
      /** The figure button's accessible name, before the figure's alt text. */
      expandAriaLabel: 'Expand figure',
      closeLabel: 'Close figure',
      /** Per figure, by figures.ts figureKey. */
      openEvents: events.ai_native_sdlc_reader.figure,
    },
    closing: {
      heading: 'From delivery-limited to idea-limited.',
      body: 'Take the paper with you, or see how Develocity makes verification faster and more reliable.',
      primary: { label: 'Download PDF', href: PDF_URL, fathomEvent: events.ai_native_sdlc_cta.download_pdf.click },
      secondary: { label: 'Request a demo', href: '/demo/', fathomEvent: events.ai_native_sdlc_cta.demo.click },
    },
  },
} as const;
